<template>
  <div class="tournament">
    <b-loading :is-full-page="false" v-model="isLoading"></b-loading>

    <TournamentCard :tournament="tournament" />

    <div
      class="is-flex mt-4"
      v-if="canEditTournament(tournament) && $route.name !== 'AdminTournament'"
    >
      <b-button
        class="mr-2"
        label="Edit tournament"
        type="is-success"
        icon-left="edit"
        @click="editTournament(tournament)"
      />
      <router-link :to="`/admin/tournaments/${tournamentId}`">
        <button class="button">Go to Admin Tournament</button>
      </router-link>
    </div>

    <b-modal
      v-model="isComponentModalActive"
      has-modal-card
      trap-focus
      :destroy-on-hide="true"
      :can-cancel="false"
    >
      <template #default="props">
        <TournamentForm :tournament="formProps" @close="props.close" />
      </template>
    </b-modal>

    <!-- LIVE MATCHES -->
    <section
      v-if="currentRoundNumber && currentRoundNumber <= tournament.totalRounds"
      class="tournament__live mt-5"
    >
      <router-link
        :to="`/tournaments/${tournamentId}/rounds/${currentRoundNumber}?live=1`"
      >
        <p class="title is-4 has-text-success">
          ROUND {{ currentRoundNumber }}: {{ currentRoundStats.live }} LIVE
          MATCHES / {{ currentRoundStats.total }} BOARDS
        </p>
      </router-link>
      <div class="tournament__matches">
        <div
          v-for="matchId in currentRoundStats.liveMatchIds.slice(0, 3)"
          :key="matchId"
        >
          <GameView :gameId="matchId" />
        </div>
      </div>
    </section>

    <!-- CONTROLLER -->
    <div
      v-if="!isLoading"
      class="is-flex is-justify-content-space-between mt-5"
      style="border-bottom: 2px solid grey"
    >
      <!-- Select Round Number -->
      <span>
        <b-field class="is-flex">
          <b-radio-button
            v-for="round in rounds.filter(
              (r) => r.roundNumber <= tournament.totalRounds
            )"
            :key="'round' + round.roundNumber"
            v-model="selectedRoundNumber"
            :native-value="round.roundNumber"
            :disabled="round.roundNumber > currentRoundNumber"
          >
            <div
              :class="
                round.roundNumber === currentRoundNumber
                  ? 'has-text-success'
                  : ''
              "
            >
              <p>
                <b>Round {{ round.roundNumber }}</b>
              </p>
              <p>{{ round.startTime | asTime }}</p>
            </div>
          </b-radio-button>
        </b-field>
      </span>

      <span v-if="secondsTillNextRound > 0 && secondsTillNextRound < 3600">
        <TimerDisplay :duration="secondsTillNextRound" :run="true" />
      </span>

      <!-- Select Type -->
      <span>
        <b-field class="is-flex">
          <b-button
            v-if="currentRoundNumber > tournament.totalRounds"
            type="is-primary is-outlined"
            icon-right="history"
            @click="replayStandings"
          />
          <b-radio-button v-model="tabName" native-value="Standings">
            <div>
              <p><b>STANDINGS</b></p>
              <p>{{ activePlayers.length }} players</p>
            </div>
          </b-radio-button>

          <b-radio-button
            v-model="tabName"
            native-value="Games"
            style="min-width: 106px"
          >
            <div>
              <p><b>GAMES</b></p>
              <p>{{ roundGames(selectedRoundNumber).length }} boards</p>
            </div>
          </b-radio-button>
        </b-field>
      </span>
    </div>

    <TournamentLeaderboard
      v-if="tabName === 'Standings'"
      :tournament="tournament"
      :rounds="rounds"
      :players="activePlayers"
      :games="games"
      :onlinePlayers="getOnlinePlayers"
      :standingIndex="selectedRoundNumber"
    />

    <RoundGameTable
      v-else-if="tabName === 'Games'"
      :tournamentId="tournamentId"
      :round="rounds[selectedRoundNumber - 1]"
      :games="roundGames(selectedRoundNumber)"
      :players="activePlayers"
      :currentRoundNumber="currentRoundNumber"
    />

    <TabTitle :title="tabTitle" />
  </div>
</template>

<script>
import { TOURNAMENT_GAME_RESULT } from "vue-chess-shared/constants";
import TournamentCard from "@/components/tournaments/TournamentCard.vue";
import GameView from "@/components/games/GameView.vue";
import TimerDisplay from "@/components/TimerDisplay.vue";
import TournamentLeaderboard from "@/components/tournaments/TournamentLeaderboard.vue";
import RoundGameTable from "@/components/tournaments/RoundGameTable.vue";
import TabTitle from "@/components/TabTitle.vue";
import { TournamentsAPI } from "@/services/api";
import { firestore } from "@/services/firebase";
import { mapGetters } from "vuex";
import TournamentPermission from "@/components/admins/TournamentPermission";
import gameUrls from "@/mixins/gameUrls";
import { diffIn } from "vue-chess-shared/utils/date-helper";

export default {
  name: "tournament",

  components: {
    TournamentCard,
    GameView,
    TimerDisplay,
    TournamentLeaderboard,
    RoundGameTable,
    TabTitle,
  },

  mixins: [TournamentPermission, gameUrls],

  data() {
    return {
      tournamentId: null,
      tournament: null,
      players: [],
      rounds: [],
      games: [],
      matches: [],
      secondsTillNextRound: null,
      timerInterval: null,
      isLoading: true,
      tabName: "Standings",
      selectedRoundNumber: 0,
    };
  },

  computed: {
    ...mapGetters(["getUserId", "getOnlinePlayers"]),
    ...mapGetters({
      currentPlayer: "getPlayer",
    }),

    tournamentRef() {
      if (!isNaN(this.tournamentId)) return null;
      return firestore.collection("tournaments").doc(this.tournamentId);
    },

    currentRoundNumber() {
      if (!this.tournament) return null;
      return this.tournament.currentRound;
    },

    currentRoundStats() {
      const roundGames = this.games.filter(
        (g) => g.roundNumber === this.currentRoundNumber && g.matchRef
      );
      return {
        liveMatchIds: roundGames
          .filter(
            (g) =>
              g.whiteResult == TOURNAMENT_GAME_RESULT.UNFINISHED ||
              g.blackResult == TOURNAMENT_GAME_RESULT.UNFINISHED
          )
          .map((g) => g.matchGUID),
        live: roundGames.filter(
          (g) =>
            g.whiteResult == TOURNAMENT_GAME_RESULT.UNFINISHED ||
            g.blackResult == TOURNAMENT_GAME_RESULT.UNFINISHED
        ).length,
        total: roundGames.length,
      };
    },

    activePlayers() {
      return this.players.filter((p) => !p.deletedAt);
    },

    myPlayer() {
      if (!this.activePlayers || !this.activePlayers.length) return null;
      if (!this.currentPlayer) return null;
      return this.activePlayers.find(
        (player) => player.username === this.currentPlayer.username
      );
    },

    myGames() {
      if (!this.games || !this.games.length) return [];
      if (!this.currentPlayer || !!this.games.username) return [];
      return this.games.filter(
        (g) =>
          g.whiteUsername === this.currentPlayer.username ||
          g.blackUsername === this.currentPlayer.username
      );
    },

    comingGames() {
      if (!this.myGames.length) return [];
      return this.myGames.filter(
        (g) => new Date(g.roundStartTime + "Z") > new Date()
      );
    },

    currentGame() {
      if (!this.myGames.length) return null;
      return this.myGames.find(
        (g) => g.roundNumber === this.currentRoundNumber
      );
    },

    tabTitle() {
      if (this.isLoading) return "loading...";
      if (
        this.currentRoundNumber &&
        this.currentRoundNumber <= this.tournament.totalRounds
      ) {
        return `Round ${this.currentRoundNumber}-${this.tournament.name}`;
      }
      return this.tournament.name;
    },
  },

  watch: {
    currentGame: function () {
      if (!this.currentGame) return;
      if (this.currentGame.finishTime) return;
      if (this.currentGame.whiteResult != TOURNAMENT_GAME_RESULT.UNFINISHED) {
        return;
      }
      if (this.currentGame.blackResult != TOURNAMENT_GAME_RESULT.UNFINISHED) {
        return;
      }

      this.playMatch(this.currentGame.matchGUID);
    },

    currentRoundNumber: function () {
      if (!this.currentRoundNumber) return;
      this.selectedRoundNumber = Math.min(
        this.currentRoundNumber,
        this.tournament.totalRounds
      );
      if (this.currentRoundNumber <= this.tournament.totalRounds) {
        this.tabName = "Games";
      } else this.tabName = "Standings";
    },
  },

  methods: {
    async getTournament() {
      if (!this.tournamentId) return;
      if (!this.isLoading) return;

      if (isNaN(this.tournamentId)) {
        this.tournamentRef.get().then((snapshot) => {
          this.tournament = snapshot.data();
          this.tournament.id = snapshot.id;
          this.isLoading = false;
        });
        await this.$bind("tournament", this.tournamentRef);
        await this.$bind("players", this.tournamentRef.collection("players"));
        await this.$bind("rounds", this.tournamentRef.collection("rounds"));
        await this.$bind("games", this.tournamentRef.collection("games"));

        this.rounds.sort((a, b) => a.roundNumber - b.roundNumber);

        return;
      }
      await TournamentsAPI.getTournament(this.tournamentId, true).then(
        (data) => {
          if (data) {
            // data.currentRound = 3; //TODO: remove hack
            // data.rounds[2].startTime = "2021-02-25T05:46:00"; //TODO: remove hack
            this.tournament = data;
            this.players = data.players ? data.players : [];
            this.rounds = data.rounds ? data.rounds : [];
            this.games = data.games ? data.games : [];
            this.isLoading = false;
          }
        }
      );
    },

    roundGames(roundNumber) {
      return this.games
        .filter(
          (g) =>
            g.roundNumber === roundNumber &&
            (g.whiteUsername || g.blackUsername)
        )
        .sort((a, b) => a.board - b.board);
    },

    playMatch(matchId) {
      this.$router.push({
        path: this.getTournamentGameUrl(this.tournamentId, matchId),
      });
    },

    startNextRoundTimer() {
      this.stopTimer();

      this.timerInterval = setInterval(() => {
        if (this.currentRoundNumber == null) return;
        if (!this.rounds.length) return;
        if (this.currentRoundNumber > this.tournament.totalRounds) {
          console.log("Tournament's over. Stop timer");
          return this.stopTimer();
        }

        if (this.currentRoundNumber < this.tournament.totalRounds) {
          this.secondsTillNextRound = diffIn(
            this.rounds[this.currentRoundNumber].startTime,
            new Date(),
            "second"
          );
          return;
        }

        if (this.currentRoundNumber === this.tournament.totalRounds) {
          // last round
          this.secondsTillNextRound =
            diffIn(
              this.rounds[this.currentRoundNumber - 1].startTime,
              new Date(),
              "second"
            ) +
            2 * (1 + this.tournament.baseTimeControl) * 60;
        }
      }, 1000);
    },

    stopTimer() {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
      this.secondsTillNextRound = null;
    },

    replayStandings() {
      if (this.standingTimerInterval) return;
      this.selectedRoundNumber = 0;
      this.standingTimerInterval = setInterval(() => {
        if (
          !this.activePlayers.length ||
          !this.rounds.length ||
          !this.games.length
        ) {
          return;
        }
        if (!this.tournament) return;
        if (
          this.selectedRoundNumber >=
          Math.min(this.currentRoundNumber, this.tournament.totalRounds)
        ) {
          clearInterval(this.standingTimerInterval);
          this.standingTimerInterval = null;
          return;
        }
        ++this.selectedRoundNumber;
      }, 1000);
    },
  },

  created() {
    this.tournamentId = this.$route.params.tournamentId;
    this.getTournament().then(this.startNextRoundTimer);
  },

  beforeDestroy() {
    this.stopTimer();
  },
};
</script>

<style scoped>
.tournament {
  position: relative;
}

.tournament__matches {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
}

.round--active {
  background-color: lightblue !important;
}

.card-header {
  background-color: #e4e1ff;
}

.tabItem {
  height: 4rem;
}
</style>
